import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../supabaseClient';
import StorePublicPageRenderer from './StorePublicPageRenderer';

const STORAGE_BRIDGE = (tenantId, restaurantName, isOwner, isStoreOwner, accessToken) => `
  <script>
    (function () {
      var tenantId = ${JSON.stringify(tenantId)};
      var restaurantName = ${JSON.stringify(restaurantName || '')};
      var exempt = { ss_supabase_v1: true, ss_currency_v1: true };
      window.__SS_URL__ = ${JSON.stringify(SUPABASE_URL)};
      window.__SS_ANON_KEY__ = ${JSON.stringify(SUPABASE_ANON_KEY)};
      window.__SS_ACCESS_TOKEN__ = ${JSON.stringify(accessToken || '')};

      var realStore = window.localStorage;
      var _get    = Storage.prototype.getItem.bind(realStore);
      var _set    = Storage.prototype.setItem.bind(realStore);
      var _remove = Storage.prototype.removeItem.bind(realStore);
      var _key    = Storage.prototype.key.bind(realStore);

      function mapKey(key) {
        if (typeof key !== 'string') return key;
        if (!key.startsWith('ss_')) return key;
        if (exempt[key]) return key;
        return 'tenant:' + tenantId + ':' + key;
      }

      var proxied = {
        getItem:    function(key) { return _get(mapKey(key)); },
        setItem:    function(key, val) { _set(mapKey(key), val); },
        removeItem: function(key) { _remove(mapKey(key)); },
        key:        function(i) { return _key(i); },
        clear:      function() {},
        get length() { return realStore.length; }
      };

      try {
        Object.defineProperty(window, 'localStorage', {
          get: function() { return proxied; },
          configurable: true
        });
      } catch(e) {
        realStore.getItem    = function(key) { return _get(mapKey(key)); };
        realStore.setItem    = function(key, val) { _set(mapKey(key), val); };
        realStore.removeItem = function(key) { _remove(mapKey(key)); };
      }

      window.__SS_TENANT_ID__ = tenantId;
      window.sessionStorage.setItem('ss_active_tenant', tenantId);

      if (restaurantName) {
        var storeKey = 'tenant:' + tenantId + ':ss_store_v1';
        var existing = _get(storeKey);
        if (!existing) {
          _set(storeKey, JSON.stringify({ storeName: restaurantName }));
        } else {
          try {
            var storeData = JSON.parse(existing);
            if (!storeData.storeName) {
              storeData.storeName = restaurantName;
              _set(storeKey, JSON.stringify(storeData));
            }
          } catch(e) {}
        }
      }

      var isAdmin = ${JSON.stringify(isOwner && !isStoreOwner)};
      var routeMap = {
        '/':               isAdmin ? '/' : null,
        '/display.html':   '/restaurant/' + tenantId + '/display',
        '/admin.html':     '/restaurant/' + tenantId + '/admin',
        '/products.html':  '/restaurant/' + tenantId + '/products',
        '/modifiers.html': '/restaurant/' + tenantId + '/modifiers',
        '/store.html':     '/restaurant/' + tenantId + '/store',
        '/style.html':     '/restaurant/' + tenantId + '/style'
      };

      function htmlToRoute(href) {
        if (!href) return null;
        var path = href.replace(/[?#].*$/, '').toLowerCase();
        if (path.charAt(0) !== '/') path = '/' + path;
        return routeMap[path] || null;
      }

      document.addEventListener('click', function(event) {
        var el = event.target;
        while (el && el.tagName !== 'A') el = el.parentElement;
        if (!el) return;
        var nextHref = htmlToRoute(el.getAttribute('href'));
        if (!nextHref) return;
        event.preventDefault();
        event.stopPropagation();
        if (window.top) window.top.postMessage({ type: 'ss_navigate', href: nextHref }, '*');
      }, true);
    })();
  </script>
`;

const PublicStorePage = ({ session }) => {
  const { slug, pageKey } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isStoreOwner, setIsStoreOwner] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [srcDoc, setSrcDoc] = useState('');

  useEffect(() => {
    supabase
      .from('restaurants')
      .select('id, name, owner_id, store_owner_id')
      .eq('slug', slug)
      .single()
      .then(({ data: rest }) => {
        if (!rest) { setNotFound(true); return; }
        setRestaurant(rest);
        const uid = session?.user?.id;
        const isServsolo = uid === rest.owner_id;
        const isStoreOp = uid === rest.store_owner_id;
        setIsOwner(isServsolo || isStoreOp);
        setIsStoreOwner(isStoreOp && !isServsolo);
      });
  }, [slug, session]);

  const fileName = isOwner ? 'admin.html' : 'display.html';

  const bridge = useMemo(
    () => restaurant ? STORAGE_BRIDGE(restaurant.id, restaurant.name, isOwner, isStoreOwner, session?.access_token) : '',
    [restaurant, isOwner, isStoreOwner, session?.access_token]
  );

  useEffect(() => {
    if (!restaurant) return;
    let active = true;
    fetch(`/${fileName}`)
      .then(r => r.text())
      .then(html => {
        const injected = html.includes('<head>')
          ? html.replace('<head>', `<head><base href="/">${bridge}`)
          : `${bridge}${html}`;
        if (active) setSrcDoc(injected);
      });
    return () => { active = false; };
  }, [bridge, fileName, restaurant]);

  // Sync store name changes back to Supabase (owner only)
  useEffect(() => {
    if (!restaurant || !isOwner) return;
    const tenantKey = `tenant:${restaurant.id}:ss_store_v1`;
    const handleStorage = (e) => {
      if (e.key !== tenantKey || !e.newValue) return;
      try {
        const data = JSON.parse(e.newValue);
        const newName = (data.storeName || '').trim();
        if (!newName) return;
        supabase.from('restaurants').update({ name: newName }).eq('id', restaurant.id);
      } catch (_) {}
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [restaurant, isOwner]);

  if (notFound) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#334155' }}>
        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🏪</div>
        <h1 style={{ margin: '0 0 8px', fontSize: '1.5rem', fontWeight: 800 }}>Store not found</h1>
        <p style={{ margin: 0, color: '#64748b' }}>The store <strong>{slug}</strong> doesn't exist.</p>
      </div>
    );
  }

  if (pageKey && restaurant) {
    return <StorePublicPageRenderer restaurantId={restaurant.id} pageKey={pageKey} storeName={restaurant.name} />;
  }

  if (!srcDoc) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#64748b' }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', background: '#fff' }}>
      <iframe
        key={`${restaurant?.id}-${fileName}`}
        title={restaurant?.name || slug}
        srcDoc={srcDoc}
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </div>
  );
};

export default PublicStorePage;
